import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const BLANK = { title: "", message: "", severity: "warning", active: true };

export function AlertsTab() {
  const [alerts, setAlerts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const load = useCallback(() => api.get("/alerts").then((r) => setAlerts(r.data)).catch((e) => console.error("alerts load", e)), []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.title || !form.message) return toast.error("Title and message required");
    try { await api.post("/alerts", form); toast.success("Alert published"); setOpen(false); setForm(BLANK); load(); }
    catch (e) { console.error("create alert", e); toast.error("Could not create alert"); }
  };
  const toggle = async (a) => {
    try { await api.put(`/alerts/${a.id}`, { ...a, active: !a.active }); load(); toast.success(!a.active ? "Alert turned ON" : "Alert turned OFF"); }
    catch (e) { console.error("toggle alert", e); toast.error("Could not update"); }
  };
  const remove = async (id) => { try { await api.delete(`/alerts/${id}`); load(); toast.success("Alert deleted"); } catch (e) { console.error("delete alert", e); toast.error("Error"); } };

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">Publish firm-wide banner alerts (court closures, weather, outages) and toggle them on or off.</p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-alert-btn"><Plus className="h-4 w-4 mr-1.5" /> New Alert</Button></DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>New Company Alert</DialogTitle><DialogDescription className="sr-only">Create a banner alert.</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs">Title</Label><Input data-testid="alert-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
              <div><Label className="text-xs">Message</Label><Textarea data-testid="alert-message" rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} /></div>
              <div><Label className="text-xs">Severity</Label>
                <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                  <SelectTrigger data-testid="alert-severity"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="danger">Danger (red)</SelectItem>
                    <SelectItem value="warning">Warning (amber)</SelectItem>
                    <SelectItem value="info">Info (blue)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2"><Switch data-testid="alert-active" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /><Label className="text-sm">Show immediately</Label></div>
            </div>
            <DialogFooter><Button data-testid="save-alert-btn" onClick={create}>Publish</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="border border-border rounded-sm overflow-hidden" data-testid="alerts-table">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/60"><TableHead>Live</TableHead><TableHead>Title</TableHead><TableHead>Message</TableHead><TableHead>Severity</TableHead><TableHead></TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {alerts.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-8">No alerts yet.</TableCell></TableRow>}
            {alerts.map((a) => (
              <TableRow key={a.id} data-testid="alert-row" className={a.active ? "" : "opacity-60"}>
                <TableCell><Switch data-testid="alert-toggle" checked={!!a.active} onCheckedChange={() => toggle(a)} /></TableCell>
                <TableCell className="font-medium">{a.title}</TableCell>
                <TableCell className="text-sm text-muted-foreground max-w-[320px] truncate">{a.message}</TableCell>
                <TableCell><span className="text-[11px] px-1.5 py-0.5 rounded-sm uppercase font-semibold bg-primary/10 text-primary">{a.severity}</span></TableCell>
                <TableCell><button data-testid="delete-alert-btn" onClick={() => remove(a.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </>
  );
}
